import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { GWElement } from 'lib/elements'
import { imagesLoaded, fromCamel, range, getSectionId, callOnLoad } from 'utils/gw'
gsap.registerPlugin(ScrollToPlugin)

const PAGE_STATUSES = {
  error: 'error',
  fetching: 'fetching',
  ready: 'ready',
  complete: 'complete',
}

class InfiniteGrid extends GWElement {
  _loading = false
  _rendering = false
  pages = {}
  renderQueue = []

  constructor() {
    super()
    this.setBaseUrl()
    this.sectionId = getSectionId(this)
  }

  get optionDefaults() {
    return {
      manual: false,
      offset: 2000,
      itemsPerPage: null,
      type: 'product',
      selectors: {
        grid: '[data-columns], .gw-grid, .grid, .product-grid',
        gridItems: null,
        loadMore: 'load-more',
        stickyHeader: 'sticky-header',
      },
    }
  }

  derivedOptions(options) {
    return {
      identifier: `${options.type}Id`,
      selectors: {
        card: `[data-gw-${options.type}-card-selector]`,
      },
    }
  }

  static get observedAttributes() {
    return ['page']
  }

  attributeChangedCallback(name, previousValue, value) {
    if (name === 'page' && Number(value) !== this.page) {
      this.page = Number(value)
    }
  }

  setBaseUrl() {
    const url = new URL(location)
    url.searchParams.delete('page')
    this.baseURL = url
  }

  init() {
    super.init()

    console.debug('Infinite Grid | Initializing...')

    this.id = `InfiniteGrid-${this.sectionId}`
    this.itemsPerPage = this.options.itemsPerPage ?? this.gridItemElements.length
    this.loadedPages ??= []
    this.renderedPages ??= []
    this.cardPageTriggers ??= new Map()

    this.observer = new IntersectionObserver(this.observe.bind(this), {
      rootMargin: `0px 0px ${this.options.offset}px 0px`,
    })

    this.pageTriggerObserver = new IntersectionObserver(this.observePageTriggers.bind(this))

    this.handleClick = this.onClick.bind(this)

    if (!this.pagination) {
      console.warn('Infinite Grid | No pagination element found on init.')
      return
    }

    console.debug('Infinite Grid | Pagination: Connecting...')

    this.initCardSliderObserver()

    this.pagination.connect(this)

    this.pages[this.page] = {
      page: this.page,
      status: PAGE_STATUSES.complete,
    }

    this.pagination.bindEvents(this.handleClick)
    this.activateObserver()

    if (this.link?.dataset.loadMorePending === 'true') {
      delete this.link.dataset.loadMorePending
      this.handleClick(new Event('click', { cancelable: true }))
    }

    if (this.page > 1) {
      this.setPageAnchor(this.getElement('card', { parent: this.gridElement, cache: false }), this.page)
      this.loadPreviousPages()
    }

    callOnLoad(this.fetchOnIdle.bind(this))
  }

  initCardSliderObserver() {
    const items = this.getItemsFromContainer(this)

    this.cardSliderObserver = new IntersectionObserver(this.onCardObservation.bind(this))

    this.observeItems(items)
  }

  observeItems(items) {
    for (const item of items) {
      this.cardSliderObserver.observe(item)
    }
  }

  onCardObservation(entries, observer) {
    for (const entry of entries) {
      InfiniteGrid.toggleCardSlider(entry.target, entry.isIntersecting)
    }
  }

  static toggleCardSlider(item, enable = true) {
    const slider = item.querySelector('.slider')
    if (!slider) return

    slider.classList.toggle('is-sleeping', !enable)
  }

  get loading() {
    return this._loading
  }

  set loading(value) {
    value = !!value
    this._loading = value

    if (value) {
      this.link?.classList.add('loading')
    } else if (!this.rendering) {
      this.link?.classList.remove('loading')
    }
  }

  get rendering() {
    return this._rendering
  }

  set rendering(value) {
    value = !!value
    this._rendering = value

    if (value) {
      this.link?.classList.add('loading')
    } else if (!this.loading) {
      this.link?.classList.remove('loading')
    }
  }

  get page() {
    return Number(this._page ?? this.getAttribute('page') ?? 1)
  }

  get nextPage() {
    const nextPageNumber = this.getUrlPageNumber(this.nextUrl)

    if (nextPageNumber) {
      return nextPageNumber
    }

    if (!this.nextUrl) {
      return null
    }

    console.warn('Failed to determine next page number from URL:', this.nextUrl)
    return this.page + 1
  }

  getUrlPageNumber(url) {
    if (!url) return null

    let page = null
    try {
      page = new URL(url, location.origin).searchParams.get('page')
    } catch {
      return null
    }

    if (!page || !/^\d+$/.test(page)) return null

    return Number(page)
  }

  set page(value) {
    this._page = value
    this.setAttribute('page', this._page)
    const url = new URL(location)
    if (this._page !== 1) {
      url.searchParams.set('page', this._page)
    } else {
      url.searchParams.delete('page')
    }
    history.pushState({}, '', url)
  }

  get automatic() {
    return !this.options.manual
  }

  get gridElement() {
    return this.getElement('grid')
  }

  get gridItemElements() {
    return this.getElement('gridItems', { all: true, parent: this.gridElement, cache: false })
  }

  get gridItemCards() {
    return this.getElement('card', { all: true, parent: this.gridElement, cache: false })
  }

  get gridItemIds() {
    return this.getItemIds(this.gridItemElements)
  }

  get pagination() {
    return this.getElement('loadMore')
  }

  get statusRegion() {
    return document.querySelector('[data-product-grid-status]')
  }

  get busyRegion() {
    return this.closest('[id$="ProductGrid"]')
  }

  setBusy(value) {
    this.busyRegion?.setAttribute('aria-busy', value ? 'true' : 'false')
  }

  announceResults() {
    const status = this.statusRegion
    if (!status) return

    const shown = this.gridItemCards.length
    const total = Number(status.dataset.total) || shown
    const template = shown === 1 ? status.dataset.messageOne : status.dataset.messageOther
    if (!template) return

    status.textContent = ''
    setTimeout(() => {
      status.textContent = template
        .replace('__shown__', String(shown))
        .replace('__total__', String(total))
    }, 50)
  }

  restoreManualFocus() {
    const focusTarget = this._manualFocusTarget
    this._manualFocusTarget = null
    if (!focusTarget?.isConnected) return

    setTimeout(() => {
      if (focusTarget.isConnected) focusTarget.focus({ preventScroll: false })
    }, 50)
  }

  getElementFromContainer(parent, key, { all = false } = {}) {
    return this.getElement(key, { parent, all, cache: false })
  }

  getItemsFromContainer(container) {
    if (!container) {
      console.warn('Infinite Grid | Failed to get items from container, no container provided')
      return null
    }

    const grid = this.getElementFromContainer(container, 'grid')
    return this.getElementFromContainer(grid, 'gridItems', { all: true })
  }

  // TODO: Update and abstract into core element method
  getElement(key, { all = false, parent = this, cache = true } = {}) {
    const selector = this.options.selectors[key]

    let result
    if (selector) {
      const method = all ? 'querySelectorAll' : 'querySelector'
      result = parent[method](selector)
    } else {
      result = parent.children
    }

    if (!result) return null

    const returnValue = all ? [...result] : result

    return cache ? this.cacheElement(key, returnValue) : returnValue
  }

  cacheElement(key, value) {
    if (!key.startsWith('_')) key = `_${key}`

    this.elements ??= {}
    this.elements[key] ??= value

    return this.elements[key]
  }

  clearElementCache() {
    this._grid = undefined
    this._gridItems = undefined
    this._loadMore = undefined
  }

  selectById(id) {
    return this.gridElement.querySelector(`[data-${fromCamel(this.options.identifier)}="${id}"]`)
  }

  setPageAnchor(cardId, page) {
    const card = cardId instanceof HTMLElement ? cardId : this.selectById(cardId)
    if (!card) return;
    console.debug('Infinite Grid | Setting page anchor', card, page)
    this.cardPageTriggers.set(card, Number(page))
    this.pageTriggerObserver.observe(card)
  }

  observe(entries, observer) {
    if (this.isBackfilling) return

    const entry = entries[0]

    if (entry.isIntersecting) {
      let nextPage = this.nextPage

      if (!nextPage) {
        this.end()
        return
      }

      while (this.pages[nextPage]?.status === PAGE_STATUSES.complete) {
        console.debug('Infinite Grid | Observe: Next page already loaded. Incrementing next page...')
        nextPage++
        // console.debug('Infinite Grid | Observe: Next page already loaded. Skipping...')
        // return
      }

      // if (this.pages[nextPage]?.status === PAGE_STATUSES.complete) {
      //   console.debug('Infinite Grid | Observe: Next page already loaded. Skipping...')
      //   return
      // }

      console.debug('Infinite Grid | Observe: Checking page status', nextPage, this.pages[nextPage])

      if (this.pageNumbers.includes(nextPage)) {
        if (this.automatic) this.renderPage(nextPage)
      } else {
        this.load({ page: nextPage, render: this.automatic })
      }

      // if (this.pages[nextPage]?.status !== PAGE_STATUSES.ready) {
      //   this.load({ render: this.automatic })
      // } else {
      //   this.renderPage()
      // }

      observer.disconnect()
    }
  }

  // Update current page based on scroll direction and trigger visibility
  observePageTriggers(entries, observer) {
    const entry = entries[0]

    if (this.isBackfilling) return

    const pageModifier = !entry.isIntersecting && window.scrollDirection === 'up' ? -1 : 0

    this.setPageFromTrigger(entry.target, pageModifier)
  }

  setPageFromTrigger(trigger, modifier = 0) {
    const page = Math.max(1, this.cardPageTriggers.get(trigger) + modifier)

    if (page !== this.page) {
      this.page = page
    }
  }

  get link() {
    return this.pagination?.link ?? null
  }

  get nextUrl() {
    return this.pagination?.url ?? null
  }

  async loadPreviousPages() {
    this.isBackfilling = true

    // Store first item for scroll position lock
    const firstItem = this.getElement('card', { parent: this.gridElement, cache: false })
    const stickyHeader = this.getElement('stickyHeader', { parent: document })

    // Get previous page numbers in reverse order
    const previousPages = range(1, this.page - 1).toReversed()

    // Loop through previous pages and prepend to grid
    // TODO: Consider caching each page and applying all at once to reduce jumping (+ maybe fetch in parallel?)
    console.debug('Infinite Grid | Loading previous pages...')
    for (const page of previousPages) {
      const publishEvent = false
      const prepend = true
      console.debug('Infinite Grid | Loading previous page', page)

      await this.load({ render: false, page, publishEvent, prepend })
      console.debug(`Infinite Grid | Previous page ${page} loaded!`)
      this.updateGridItems(page)
      this.pages[page].status = PAGE_STATUSES.complete

      // TODO: Debounce this
      setTimeout(() => {
        stickyHeader?.reveal()
        stickyHeader?.lock()
        gsap.to(window, {
          scrollTo: {
            y: firstItem,
            offsetY: stickyHeader?.offsetHeight + 12,
          },
          duration: 0,
        })

        if (page === previousPages[previousPages.length - 1]) {
          this.preloadedPage = null
          setTimeout(() => (this.isBackfilling = false))
        }

        this.dispatchRenderEvent(page, { backfill: true })
      })
    }
  }

  get pageNumbers() {
    return Object.keys(this.pages).map(Number)
  }

  getPageUrl(page, { returnObj = false } = {}) {
    page = Number(page)
    const url = this.baseURL
    if (page === 1) {
      url.searchParams.delete('page')
    } else {
      url.searchParams.set('page', page)
    }

    return returnObj ? url : url.toString()
  }

  async load({ page = this.nextPage, render = false, publishEvent = true, prepend = false } = {}) {
    if (!page) {
      console.debug('Infinite Grid | Load More: No next page available. Skipping load...')
      return
    }

    if (this.pageNumbers.includes(page)) {
      console.debug(`Infinite Grid | Load More: Page ${page} already triggered load. Skipping...`)
      return
    }

    const url = this.getPageUrl(page, { returnObj: true })

    this.pages[page] = {
      page,
      url: url.toString(),
      status: 'fetching',
      prepend,
      content: null,
    }

    url.searchParams.set('section_id', this.sectionId)

    try {
      console.debug(`Infinite Grid | Load More: Fetch triggered. Fetching page ${page} from "${url}"...`)
      this.loading = true

      const response = await fetch(url)

      const markup = await response.text()
      const parser = new DOMParser()

      const content = parser.parseFromString(markup, 'text/html').querySelector('infinite-grid')

      // this.setPageContent(page, this.getElementFromContainer(content, 'grid'))
      this.setPageContent(page, content)

      console.debug(`Infinite Grid | Load More: Fetch complete. Page ${page} fetched.`)

      if (publishEvent) {
        this.dispatchFetchEvent(page)
      }

      if (render) {
        this.renderPage(page)
      } else {
        this.loading = false
      }
    } catch (error) {
      console.error('Infinite Grid | Load More: Error fetching page:', error)
      this.pages[page].status = PAGE_STATUSES.error
      this.setBusy(false)
      if (publishEvent) this.dispatchFetchEvent(page)
      this.loading = false
      if (render) this.rendering = false
    }
  }

  setPageContent(page, content) {
    this.pages[page].content = content
    this.pages[page].status = PAGE_STATUSES.ready
  }

  async waitForPage(page, event = 'fetch') {
    const eventName = `infinite-grid:${event}`

    return new Promise(resolve => {
      const listenerCallback = e => {
        const { id, page: eventPage } = e.detail

        if (id === this.id && eventPage === page) {
          this.removeEventListener(eventName, listenerCallback)
          console.info(`Infinite Grid | Wait for Page: Page ${page} has ${event}ed.`)
          resolve()
        }
      }

      this.addEventListener(eventName, listenerCallback)
    })
  }

  async renderPage(page = this.nextPage) {
    this.rendering = true
    this.loading = false
    // TODO: Remove this if you figure out why it's not applying from rendering setter
    this.link?.classList.add('loading')

    if (!page) {
      if (!this.renderQueue.length) {
        console.debug('Infinite Grid | Render Page: Render queue empty. Skipping render...')
        this.rendering = false
        this.loading = false
        return
      }

      page = this.renderQueue.shift()
    }

    if (!this.pages[page]) {
      return this.load({ page, render: true })
    }

    let { status, content, prepend } = this.pages[page]
    const adjacentPage = this.pages[page + (prepend ? 1 : -1)]

    if (adjacentPage && adjacentPage.status !== PAGE_STATUSES.complete) {
      console.warn('Infinite Grid | Render Page: Previous page not yet rendered. Waiting...')
      await this.waitForPage(adjacentPage.page, 'render')
    }

    if (status === PAGE_STATUSES.complete) {
      return this.renderPage(page + 1)
    }

    if (status === PAGE_STATUSES.fetching) {
      console.debug(`Infinite Grid | Render Page: Page ${page} is currently fetching. Waiting...`)
      await this.waitForPage(page, 'fetch')
      const pageState = this.pages[page]
      status = pageState.status
      content = pageState.content
      prepend = pageState.prepend
    }

    if (status !== PAGE_STATUSES.ready || !content) {
      console.warn(`Infinite Grid | Render Page: Page ${page} is not ready (status: ${status}). Skipping render...`)
      this.rendering = false
      this.loading = false
      return
    }

    if (!this.isBackfilling) {
      console.debug('Infinite Grid | Update: Preloading images...')
      await this.preloadImages(content)
    }
    console.debug('Infinite Grid | Update: Images preloaded.')

    this.updateGridItems(page)

    const hasNextPage = this.updatePagination(content)
    if (this.options.manual && !prepend) this.restoreManualFocus()

    this.pages[page].content = null

    setTimeout(() => this.dispatchRenderEvent(page))

    this.rendering = false
    // TODO: Remove this if you figure out why it's not applying from rendering setter
    this.link?.classList.remove('loading')

    if (hasNextPage) {
      this.fetchOnIdle()
    }
  }

  getPageContent(page) {
    if (!page || !this.pages[page]) {
      console.warn('Infinite Grid | Get Page Content: Invalid page number or page not found. Returning null.')
      return null
    }

    return this.getItemsFromContainer(this.pages[page].content)
  }

  dispatchRenderEvent(page, { backfill = false } = {}) {
    this.dispatchEvent(
      new CustomEvent('infinite-grid:render', {
        detail: {
          backfill,
          id: this.id,
          page,
        },
      })
    )
  }

  dispatchFetchEvent(page) {
    this.dispatchEvent(
      new CustomEvent('infinite-grid:fetch', {
        detail: {
          id: this.id,
          page,
        },
      })
    )
  }

  async preloadImages(container) {
    if (!container) return

    await imagesLoaded(this.getItemsFromContainer(container).map(item => item.querySelector('img')))
  }

  fetchOnIdle() {
    requestIdleCallback(async () => {
      if (this.loading || this.rendering) return

      const nextPage = this.nextPage
      if (!nextPage) return

      await this.load({ page: nextPage, render: false })
    })
  }

  getItemId(element, page = this.page) {
    return element.dataset[this.options.identifier] ?? element.firstElementChild.dataset[this.options.identifier]
  }

  getItemIds(elements = this.gridItemElements) {
    return elements.map(el => this.getItemId(el))
  }

  updateGridItems(page) {
    console.debug('Infinite Grid | Update Grid Items | Update triggered', page, this.pages)
    const { content, prepend } = this.pages[page]

    if (!content) {
      console.warn('Infinite Grid | Update Grid Items: No content found for page. Refetching...', page)
      delete this.pages[page]
      this.load({ page, render: true })
      return
    }

    const grid = this.getElementFromContainer(content, 'grid')
    const existingItems = [...this.gridItemElements]

    const position = prepend ? 'afterbegin' : 'beforeend'
    this.gridElement.insertAdjacentHTML(position, grid.innerHTML)
    console.debug('Infinite Grid | Update Grid Items | Content appended')
    this.pages[page].status = PAGE_STATUSES.complete
    console.debug(`Infinite Grid | Update Grid Items | Page ${page} status updated: ${this.pages[page].status}`)

    const addedItems = this.gridItemElements.filter(item => !existingItems.includes(item))

    this.setPageAnchor(addedItems[0], page)

    this.observeItems(addedItems)

    if (!prepend) {
      const animatedItems = this.options.manual ? addedItems.slice(1) : addedItems
      this.animateVisibleItems(animatedItems)
    }

    if (this.options.manual && !prepend) {
      const firstAddedItem = addedItems[0]
      if (firstAddedItem) {
        this._manualFocusTarget = firstAddedItem.querySelector('a.js-anchor') || firstAddedItem
        if (this._manualFocusTarget === firstAddedItem) firstAddedItem.setAttribute('tabindex', '-1')
      }

      this.setBusy(false)
      this.announceResults()
    }
  }

  animateVisibleItems(items) {
    if (!items || items.length === 0) return

    const visibleItems = []
    const hiddenItems = []

    // Set initial state for all items
    gsap.set(items, {
      autoAlpha: 0,
      scale: 0.9,
    })

    // Create intersection observer to detect visible items
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            visibleItems.push(entry.target)
          } else {
            hiddenItems.push(entry.target)
          }
        })

        // Disconnect observer once all items have been checked
        if (visibleItems.length + hiddenItems.length === items.length) {
          visibilityObserver.disconnect()

          // Show hidden items immediately without animation
          if (hiddenItems.length > 0) {
            gsap.set(hiddenItems, {
              autoAlpha: 1,
              scale: 1,
            }).then(() => {
              gsap.set(hiddenItems, { clearProps: 'opacity,scale' })
            })
          }

          // Animate visible items with stagger
          if (visibleItems.length > 0) {
            gsap.fromTo(
              visibleItems,
              {
                autoAlpha: 0,
                scale: 0.9,
              },
              {
                autoAlpha: 1,
                scale: 1,
                duration: 1,
                ease: 'power2.inOut',
                stagger: {
                  each: 0.1,
                  from: 'start',
                  grid: 'auto',
                },
                onComplete: () => {
                  visibleItems.forEach(item => {
                    gsap.set(item, { clearProps: 'opacity,visibility,scale' })
                  })
                },
              }
            )
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    )

    // Observe all items
    items.forEach(item => {
      visibilityObserver.observe(item)
    })
  }

  activateObserver() {
    if (!this.pagination) {
      console.debug('Infinite Grid | No pagination element found, skipping observation.')
      return
    }

    this.observer.observe(this.pagination)
  }

  // TODO: CLEAN UP
  updatePagination(content) {
    console.debug('Infinite Grid | Updating pagination...')

    let newPagination = null
    if (content) {
      newPagination = this.getElementFromContainer(content, 'loadMore')
      if (this.loading || this.rendering) newPagination?.querySelector('a')?.classList.add('loading')
    }

    if (newPagination) {
      const hasNextPage = this.pagination.update(newPagination, this.handleClick)
      if (!hasNextPage) {
        this.end()
        return false
      }
    } else {
      this.pagination?.replace(null)
      this.end()
      return false
    }

    this.activateObserver()

    return true
  }

  resetPagination() {
    const content = this.pages[1].content

    this.updatePagination(content)
    this.setAttribute('page', 1)
    this.destroy()
    this.pages = { 1: { content, status: PAGE_STATUSES.complete } }
    this.init(true)
  }

  onClick(e) {
    e.preventDefault()

    if (this.rendering) return

    this.setBusy(true)

    const nextPage = this.nextPage

    if (!nextPage) {
      this.setBusy(false)
      this.end()
      return
    }

    if (this.pages[nextPage]?.status === PAGE_STATUSES.error) {
      delete this.pages[nextPage]
    }

    this.renderPage(nextPage)
  }

  end() {
    this.observer.disconnect()
  }

  destroy() {
    // this.trigger?.kill()
    this.observer.disconnect()
  }

}

if (!customElements.get('infinite-grid')) {
  customElements.define('infinite-grid', InfiniteGrid)
}

class LoadMore extends GWElement {
  get optionDefaults() {
    return {
      automatic: true,
    }
  }

  static get observedAttributes() {
    return ['automatic']
  }

  get link() {
    return this.querySelector('[name="load-more"]')
  }

  get url() {
    return this.link?.getAttribute('href') ?? null
  }

  connect(grid, { automatic = !grid.manual, onClick = grid.handleClick } = {}) {
    this.grid = grid
    this.setAttribute('automatic', automatic)
    this.onClick = onClick
  }

  update(element, onClick) {
    this.replace(element)

    if (onClick) {
      this.bindEvents(onClick)
    }

    return !!this.link
  }

  replace(element) {
    if (element === null) {
      // TODO: Maybe remove this line, it no longer seems necessary
      this.innerHTML = ''
      return
    }

    if (!element) {
      console.info('Skipped replacement of "load more" pagination element, no element provided.')
      return
    }

    this.innerHTML = element.innerHTML

    return
  }

  bindEvents(onClick = this.onClick) {
    if (!onClick) {
      console.warn('Failed to bind LoadMore events, no onClick function provided.')
      return
    }

    // if (this.automatic) return

    this.link?.addEventListener('click', onClick)
  }

  // toggleVisibility() {
  //   this.link.toggleAttribute('hidden', this.automatic)
  // }

  // init() {
  //   super.init()
  // }
}

if (!customElements.get('load-more')) {
  customElements.define('load-more', LoadMore)
}
